import React, { useState } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Sliders,
  Camera,
  FileEarmarkText,
  Play,
  Icon,
} from "react-bootstrap-icons";
import "./Demo.css";

interface DemoFeature {
  id: string;
  title: string;
  description: string;
  videoSrc: string;
  icon: Icon;
}

const Demo: React.FC = () => {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const demoFeatures: DemoFeature[] = [
    {
      id: "entity-demo",
      title: "Entity Recognition Demo",
      description:
        "See how our AI identifies chemical entities from various sources in real-time.",
      videoSrc: `/videos/fyled-entity-demo.mp4`,
      icon: Sliders,
    },
    {
      id: "draw-search",
      title: "Draw & Search Demo",
      description:
        "Watch how easy it is to draw molecular structures and search our database.",
      videoSrc: `/videos/fyled-image-search.mp4`,
      icon: Sliders,
    },
    {
      id: "pdf-analysis",
      title: "PDF Analysis Demo",
      description:
        "Learn how our tool extracts chemical structures directly from research papers.",
      videoSrc: `/videos/fyled-image-search.mp4`,
      icon: Sliders,
    },
  ];

  // Function to play video in a modal or expand the card
  const playVideo = (id: string) => {
    setActiveVideo(id);
    // In a real implementation, you might use a modal library or expand the card
  };

  // Close the video player
  const closeVideo = () => {
    setActiveVideo(null);
  };

  return (
    <div className="demo-page">
      {/* Hero section */}
      <div className="demo-hero">
        <Container>
          <Row className="py-5">
            <Col lg={8} className="mx-auto text-center">
              <h1 className="display-4">Experience Fyled AI In Action</h1>
              <p className="lead mb-4">
                See how our advanced AI technology transforms chemical research
                and analysis
              </p>

              {/* React Bootstrap Button replaced with pure HTML button */}
              <a
                href="#/ketcher"
                className="btn btn-light btn-lg px-4 interactive-demo-btn"
                style={{ cursor: "pointer", zIndex: 100, position: "relative" }}
              >
                Try Interactive Demo
              </a>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Features grid */}
      <Container className="py-5">
        <h2 className="text-center mb-5">Discover Our Key Features</h2>

        <Row className="g-4">
          {demoFeatures.map((feature) => (
            <Col lg={6} key={feature.id}>
              <Card
                className={`feature-video-card ${
                  activeVideo === feature.id ? "active" : ""
                }`}
              >
                <div className="feature-card-content">
                  <div className="feature-card-bg"></div>
                  <Card.Body className="feature-card-body">
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>

                    <Button
                      variant="primary"
                      className="play-button"
                      onClick={() => playVideo(feature.id)}
                    >
                      <Play size={20} /> Play
                    </Button>
                  </Card.Body>
                </div>

                {activeVideo === feature.id && (
                  <div className="video-player-container">
                    <Button
                      variant="light"
                      className="close-video"
                      onClick={closeVideo}
                    >
                      ×
                    </Button>
                    <video
                      src={feature.videoSrc}
                      controls
                      autoPlay
                      className="feature-video"
                    />
                  </div>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Additional demo sections */}
      <div className="secondary-demo-section py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={5} className="mb-4 mb-lg-0">
              <h2>Interactive Chemical Tools</h2>
              <p className="lead">
                Our platform provides powerful tools for chemical structure
                drawing, analysis, and visualization.
              </p>
              <ul className="feature-list">
                <li>Draw and edit molecular structures</li>
                <li>Convert between different formats (SMILES, InChI, etc.)</li>
                <li>Calculate properties and descriptors</li>
                <li>3D visualization and conformer generation</li>
              </ul>
              <Button
                variant="outline-light"
                className="mt-3"
                onClick={() => handleNavigate("#/ketcher")}
              >
                Try Molecular Editor
              </Button>
            </Col>
            <Col lg={7}>
              {/* <div className="demo-preview-card">
                <img
                  src={`/images/molecular-editor-preview.png`}
                  alt="Molecular Editor Tool"
                  className="img-fluid rounded"
                />
              </div> */}
            </Col>
          </Row>
        </Container>
      </div>

      {/* CTA Section */}
      <Container className="py-5">
        <div className="cta-section text-center p-5">
          <h2 className="mb-4">Ready to transform your chemical research?</h2>
          <p className="lead mb-4">
            Join leading research organizations and pharmaceutical companies
            using Fyled AI
          </p>
          <Button
            style={{ color: "white" }}
            size="lg"
            className="me-3"
            onClick={() => handleNavigate("#/login")}
          >
            Start Free Trial
          </Button>
          <Button
            variant="secondary"
            style={{ color: "white" }}
            size="lg"
            onClick={() => handleNavigate("#/contact")}
          >
            Schedule Demo
          </Button>
        </div>
      </Container>
    </div>
  );
};

// Helper function for navigation
const handleNavigate = (path: string) => {
  window.location.href = path;
};

export default Demo;
